import { BottomAction } from '@euler/app/components/BottomAction';
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { useVoiceFeatures } from '@euler/app/components/voice/useVoiceFeatures';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useQuotationFormContext } from '@euler/app/flows/task/pages/quotation/components/quotation-form/Context';
import {
  kMaterialTypeOptions,
  kQuotationMaterialInventoryStatusOptions,
} from '@euler/app/flows/task/pages/quotation/functions/constants';
import { keyOfItem } from '@euler/app/flows/task/pages/quotation/functions/utils';
import { Center } from '@euler/components/Center';
import { Input } from '@euler/components/form/Input';
import { InputNumber } from '@euler/components/form/InputNumber';
import { Stepper } from '@euler/components/form/Stepper';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  useGeneralBottomSheetHeight,
  useGeneralBottomSheetModal,
} from '@euler/functions/useBottomSheetModal';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import {
  FormController,
  FormState,
  useFormController,
} from '@euler/lib/form/controller';
import { useToggleAutomaticKeyboard } from '@euler/lib/keyboard';
import {
  MaterialType,
  QuotationItemType,
  QuotationMaterialInventoryStatus,
} from '@euler/model/enum';
import { QuotationMaterialItemStaged } from '@euler/model/task-detail';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import {
  BottomSheetFooter,
  BottomSheetFooterProps,
  BottomSheetModal,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet';
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';
import { useTheme } from 'styled-components';
import invariant from 'tiny-invariant';
import { QuotationFormElements } from './Elements';

const { Section, Row, Select, useStyles } = QuotationFormElements;

type EditorRef = {
  controller: FormController<MaterialFormData>;
};

export type MaterialFormData = {
  name: string;
  price?: number;
  discountPrice?: number;
  qty: number;
  materialType?: MaterialType;
  partNumber?: string;
  brandName?: string;
  inventoryStatus?: QuotationMaterialInventoryStatus;
  discountRemark?: string;
  remark?: string;
};

type Props = {
  staged?: QuotationMaterialItemStaged;
  isNamePermitted: (
    name: string,
    values: Readonly<FormState<MaterialFormData>>,
  ) => boolean;
};

export const MaterialEditor = memo(
  forwardRef<EditorRef, Props>(({ staged, isNamePermitted }, ref) => {
    const { rpx } = useResponsiveContext();
    const theme = useTheme();
    const styles = useStyles();
    const { isVoiceInputEnabled } = useVoiceFeatures();

    const {
      inspection: {
        customIssue: { placeholderColor },
      },
    } = theme;

    const formController = useFormController<MaterialFormData>({
      name: {
        required: true,
        defaultValue: staged?.name ?? '',
        validators: [
          (value, values) => {
            if (!value.trim()) return true;
            if (!isNamePermitted(value, values)) {
              return { error: '配件名称已存在' };
            }
            return true;
          },
        ],
      },
      price: {
        required: false,
        defaultValue: staged?.price,
        validators: [
          value => {
            if (value == null) return true;
            if (value < 0) {
              return {
                error: '价格不能小于0',
              };
            }
            return true;
          },
        ],
      },
      discountPrice: {
        defaultValue: staged?.discountPrice,
        validators: [
          (value, data) => {
            if (value == null || data.price.value == null) {
              return true;
            }
            if (value < 0)
              return {
                error: '折扣价不能小于0',
              };
            if (value > data.price.value) {
              return {
                error: '折扣价不能大于原价',
              };
            }
            return true;
          },
        ],
      },
      qty: {
        required: true,
        defaultValue: staged?.qty ?? 1,
      },
      materialType: {
        defaultValue: staged?.materialType,
      },
      partNumber: {
        defaultValue: staged?.partNumber,
      },
      brandName: {
        defaultValue: staged?.brandName,
      },
      inventoryStatus: {
        defaultValue:
          staged?.inventoryStatus ?? QuotationMaterialInventoryStatus.InStock,
      },
      discountRemark: {
        defaultValue: staged?.discountRemark,
      },
      remark: {
        defaultValue: staged?.remark,
      },
    });

    useImperativeHandle(ref, () => ({
      controller: formController,
    }));

    const { form } = formController;

    return (
      <>
        <Section title="配件名称">
          <Row>
            <Input
              value={form.name.value}
              errorMsg={form.name.error}
              style={styles.formInput}
              onChangeText={form.name.update}
              onBlur={form.name.onBlur}
              placeholder="例如: 火花塞"
              placeholderTextColor={placeholderColor}
            />
          </Row>
        </Section>
        <Section
          title="配件价格"
          titleRight={
            <Stepper
              min={1}
              value={form.qty.value}
              inputStyle={[
                { height: rpx(20) },
                Platform.OS === 'web' ? { width: rpx(20) } : undefined,
              ]}
              onChange={form.qty.update}
              css={`
                margin-bottom: ${rpx(5)}px;
              `}
            />
          }
        >
          <Row>
            <StackLayout
              direction="row"
              justifyContent="space-between"
              divider={
                <View
                  css={`
                    width: ${StyleSheet.hairlineWidth}px;
                    align-self: stretch;
                    background-color: #ddd;
                    margin: ${rpx(-8)}px 0;
                  `}
                />
              }
            >
              <InputNumber
                value={form.price.value}
                errorMsg={form.price.error}
                onChangeValue={form.price.update}
                onBlur={form.price.onBlur}
                placeholder="配件原价"
                placeholderTextColor={placeholderColor}
                style={[styles.formInput, { paddingRight: rpx(15) }]}
                containerStyle={{ flex: 1 }}
              />
              <InputNumber
                value={form.discountPrice.value}
                errorMsg={form.discountPrice.error}
                onChangeValue={form.discountPrice.update}
                onBlur={form.discountPrice.onBlur}
                placeholder="配件折扣价"
                placeholderTextColor={placeholderColor}
                style={[styles.formInput, { paddingLeft: rpx(15) }]}
                containerStyle={{ flex: 1 }}
              />
            </StackLayout>
          </Row>
        </Section>
        <Section title="配件类型">
          <Row>
            <Select
              options={kMaterialTypeOptions}
              value={form.materialType.value}
              onChange={form.materialType.update as any}
              placeholder="选择配件类型"
              placeholderTextColor={placeholderColor}
              style={styles.formInput}
            />
          </Row>
        </Section>
        <Section title="库存状态">
          <Row>
            <Select
              options={kQuotationMaterialInventoryStatusOptions}
              value={form.inventoryStatus.value}
              onChange={form.inventoryStatus.update as any}
              placeholder="选择库存状态"
              placeholderTextColor={placeholderColor}
              style={styles.formInput}
            />
          </Row>
        </Section>
        <Section title="配件品牌">
          <Row>
            <Input
              value={form.brandName.value}
              errorMsg={form.brandName.error}
              onChangeText={form.brandName.update}
              onBlur={form.brandName.onBlur}
              placeholder="品牌名称"
              placeholderTextColor={placeholderColor}
              style={styles.formInput}
            />
          </Row>
        </Section>
        <Section title="零件件号">
          <Row>
            <Input
              value={form.partNumber.value}
              errorMsg={form.partNumber.error}
              onChangeText={form.partNumber.update}
              onBlur={form.partNumber.onBlur}
              placeholder="如: GTYL-SDSD-23423"
              placeholderTextColor={placeholderColor}
              style={styles.formInput}
            />
          </Row>
        </Section>
        <Section title="优惠备注">
          <Row>
            <Input
              value={form.discountRemark.value}
              errorMsg={form.discountRemark.error}
              onChangeText={form.discountRemark.update}
              onBlur={form.discountRemark.onBlur}
              placeholder="优惠备注信息"
              placeholderTextColor={placeholderColor}
              style={styles.formInput}
              withVoiceInput={isVoiceInputEnabled}
            />
          </Row>
        </Section>
        <Section title="项目备注">
          <Row>
            <Input
              value={form.remark.value}
              errorMsg={form.remark.error}
              onChangeText={form.remark.update}
              onBlur={form.remark.onBlur}
              placeholder="备注信息"
              placeholderTextColor={placeholderColor}
              style={styles.formInput}
              withVoiceInput={isVoiceInputEnabled}
            />
          </Row>
        </Section>
      </>
    );
  }),
);

export const MediaEditorBottomSheet = memo(() => {
  useToggleAutomaticKeyboard(true);

  const {
    taskManager: { quotationManager },
  } = useTaskContext();
  const { materialBeingEdited$ } = useQuotationFormContext();
  const [materialBeingEdited] = useBehaviorSubject(materialBeingEdited$);

  const title =
    materialBeingEdited?.type === 'edit' ? '修改配件信息' : '添加配件';

  const { rpx } = useResponsiveContext();
  const { navBarHeight, safeAreaInsets: insets } = useSystemMetrics();

  const ref = useRef<EditorRef>(null);

  const { bottomSheetModalRef, ...bottomSheetProps } =
    useGeneralBottomSheetModal({
      show: materialBeingEdited != null,
      topMargin: navBarHeight,
      minSnapPoint: '50%',
      mediumSnapPoint: '75%',
      index: 1,
      enableDismissOnClose: true,
      enablePanDownToClose: true,
      dismissWhenPressBackdrop: true,
      onDismiss: () => {
        materialBeingEdited$.next(undefined);
      },
    });
  const animatedPosition = useSharedValue(0);
  const height = useGeneralBottomSheetHeight();
  const style = useAnimatedStyle(() => {
    return {
      opacity: interpolate(
        animatedPosition.value,
        [(height * 2) / 4, height],
        [1, 0],
      ),
    };
  });

  const onCancel = usePersistFn(() => {
    bottomSheetModalRef.current?.dismiss();
  });

  const isNamePermitted = usePersistFn(
    (name: string, values: Readonly<FormState<MaterialFormData>>) => {
      invariant(materialBeingEdited != null);

      const key = keyOfItem({
        name,
        type: materialBeingEdited.item.type,
        itemNo: values.partNumber.value,
        materialType: values.materialType.value,
        brandName: values.brandName.value,
      });

      if (materialBeingEdited.type === 'new') {
        return !materialBeingEdited.item.materials.some(
          x => keyOfItem(x) === key,
        );
      }

      const index = materialBeingEdited.index;
      return !materialBeingEdited.item.materials.some(
        (x, i) => index !== i && keyOfItem(x) === key,
      );
    },
  );

  const onDone = usePersistFn(async () => {
    invariant(materialBeingEdited != null);

    const controller = ref.current?.controller;
    if (!controller) return;

    const valid = await controller.validateAll();
    if (!valid) return;

    const data = controller.getData();

    // create or update the material
    quotationManager.produce(draft => {
      const subject = draft.subjects.find(
        x => x.name === materialBeingEdited.subject.name,
      );

      const item = subject?.items.find(
        x => x.name === materialBeingEdited.item.name,
      );

      invariant(subject != null && item != null);

      const material: QuotationMaterialItemStaged = {
        name: data.name,
        price: data.price!,
        discountPrice: data.discountPrice,
        qty: data.qty,
        optionGroup: undefined,
        included: true,
        defaultIncluded: true,
        type: QuotationItemType.Material,
        brandName: data.brandName,
        materialType: data.materialType,
        partNumber: data.partNumber,
        inventoryStatus: data.inventoryStatus,
        discountRemark: data.discountRemark?.trim(),
        remark: data.remark?.trim(),
      };

      if (materialBeingEdited.type === 'new') {
        item.materials.push(material);
      } else {
        invariant(item.materials[materialBeingEdited.index] != null);
        item.materials[materialBeingEdited.index] = material;
      }

      if (!item.included) {
        item.included = true;
      }
    });

    bottomSheetModalRef.current?.dismiss();
  });

  const Footer = usePersistFn((props: BottomSheetFooterProps) => {
    return (
      <BottomSheetFooter {...props}>
        <BottomAction containerStyle={style}>
          <BottomAction.Button
            text="取消"
            variant="default"
            onPress={onCancel}
          />
          <BottomAction.Button
            text="确定"
            variant="primary"
            css={`
              margin-left: ${rpx(15)}px;
            `}
            onPress={onDone}
          />
        </BottomAction>
      </BottomSheetFooter>
    );
  });

  const Header = useCallback(
    () => (
      <Center
        css={`
          padding: ${rpx(10)}px 0;
          border-bottom-width: 1px;
          border-bottom-color: #f1f1f1;
          height: ${rpx(44)}px;
        `}
      >
        <Typography.Label size={rpx(16)} medium color="#333">
          {title}
        </Typography.Label>
      </Center>
    ),
    [rpx, title],
  );

  const ScrollViewComponent =
    Platform.OS === 'web' ? ScrollView : BottomSheetScrollView;

  return (
    <>
      <BottomSheetModal
        {...bottomSheetProps}
        footerComponent={Footer}
        animatedPosition={animatedPosition}
        keyboardBehavior="interactive"
        keyboardBlurBehavior="none"
        ref={bottomSheetModalRef}
      >
        <ScrollViewComponent
          contentContainerStyle={{
            paddingBottom: insets.bottom + rpx(90),
          }}
        >
          <Header />
          <MaterialEditor
            ref={ref}
            key={materialBeingEdited?.key}
            isNamePermitted={isNamePermitted}
            staged={
              materialBeingEdited?.type === 'edit'
                ? materialBeingEdited.material
                : undefined
            }
          />
        </ScrollViewComponent>
      </BottomSheetModal>
    </>
  );
});
